import * as R from 'ramda';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { StatusBanner } from '@xingternal/banner';

import { JobAlertContext, Summary } from '@/contexts';
import { JobPostingVisibleJobFragment, useSearchQueryIdBySearchQueryQuery } from '@/graphql/generated';
import { fromQuery, getQueryParams } from '@/helpers/query-string';
import { useLoginStates } from '@/hooks';

import { CreateAlertLightboxContainer } from './components/create-alert-lightbox';
import { useAlertLightbox } from './useAlertLightBox';

type Feedback = {
  type: string;
  message: string;
  show: boolean;
};

type StateProps = {
  feedback: Feedback;
};

type Props = {
  jobLocation?: string | null;
  discipline?: JobPostingVisibleJobFragment['discipline'];
  children: React.ReactNode;
};

const getAlertSummaryFromQuery = (queryString: string): Summary | null => {
  const queryParams = getQueryParams(queryString);
  const searchContext = fromQuery(queryParams).search_query as Summary;

  if (searchContext?.keywords) return R.pick(['keywords', 'location', 'radius'], searchContext);

  return null;
};

const PopUpHandlerComponent: React.FC<StateProps & Props> = ({ feedback, children, jobLocation, discipline }) => {
  const { isLoggedIn } = useLoginStates();
  const location = useLocation();
  const summary: Summary = getAlertSummaryFromQuery(location.search) || {
    keywords: undefined,
    location: jobLocation ?? '',
    radius: '20',
    discipline,
  };

  const { data } = useSearchQueryIdBySearchQueryQuery({
    variables: {
      query: {
        keywords: summary.keywords,
        location: { text: summary.location, radius: Number(summary.radius) },
        ...(!summary.keywords && discipline?.id && { filter: { discipline: { id: [discipline.id] } } }),
      },
      consumer: 'loggedin.web.jobs.posting.alerts',
    },
    skip: !isLoggedIn,
  });
  const searchQueryId = data?.jobSearchByQuery?.searchQuery.guid;

  const { isAlertLightboxOpen, toggleAlertLightbox, propActionOrigin } = useAlertLightbox({
    initialOrigin: 'jobs_menu',
  });

  return (
    <JobAlertContext.Provider value={{ isAlertLightboxOpen, toggleAlertLightbox, summary, propActionOrigin }}>
      {children}
      <CreateAlertLightboxContainer summary={summary} searchQueryId={searchQueryId} />
      {feedback.type === 'error' && (
        <StatusBanner show={feedback.show} variant="error">
          {feedback.message && <FormattedMessage id={feedback.message} />}
        </StatusBanner>
      )}
    </JobAlertContext.Provider>
  );
};

const mapStateToProps = ({ search }): StateProps => ({ feedback: search.notification });

export const PopUpHandler = connect(mapStateToProps)(PopUpHandlerComponent);
